import React, { forwardRef } from 'react';

/**
 * Input - Standardized input control with icon support, focus states, and error messages.
 */
export const Input = forwardRef(({
    type = 'text',
    label,
    error,
    helperText,
    leftIcon,
    LeftIcon: LeftIconProp,
    rightIcon,
    RightIcon: RightIconProp,
    onRightIconClick,
    className = '',
    containerClassName = '',
    disabled = false,
    ...props
}, ref) => {
    const LeftIcon = leftIcon || LeftIconProp;
    const RightIcon = rightIcon || RightIconProp;
    const baseContainer = `relative w-full ${containerClassName}`;
    const baseInput = `flex h-10 w-full rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-2 text-[13px] text-[var(--text-primary)] shadow-sm transition-all file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[var(--text-tertiary)] hover:border-[var(--border-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-glow)] focus-visible:border-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-50`;
    const errorInput = error ? 'border-[var(--danger)] focus-visible:ring-[var(--danger-subtle)] focus-visible:border-[var(--danger)]' : '';
    const iconPaddingLeft = LeftIcon ? 'pl-10' : '';
    const iconPaddingRight = RightIcon ? 'pr-10' : '';
    const combinedInput = `${baseInput} ${errorInput} ${iconPaddingLeft} ${iconPaddingRight} ${className}`;

    return (
        <div className="flex flex-col gap-2 w-full">
            {label && (
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {label}
                </label>
            )}
            
            <div className={baseContainer}>
                {LeftIcon && (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        <LeftIcon size={16} />
                    </div>
                )}
                <input
                    ref={ref}
                    type={type}
                    disabled={disabled}
                    className={combinedInput}
                    {...props}
                />
                {RightIcon && (
                    <button 
                        type="button"
                        onClick={onRightIconClick}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 cursor-pointer"
                        disabled={disabled}
                    >
                        <RightIcon size={16} />
                    </button>
                )}
            </div>

            {(error || helperText) && (
                <p className={`text-[0.8rem] ${error ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                    {error || helperText}
                </p>
            )}
        </div>
    );
});

Input.displayName = 'Input';
